import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  DashboardCustomize as DashboardCustomizeIcon,
  LocationOn as LocationOnIcon,
  BarChart as BarChartIcon,
  LocalGasStation as GasIcon,
  BatteryChargingFull as BatteryIcon,
  DonutLarge as PenetrationIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';

const collapsedDrawerWidth = 56;
const expandedDrawerWidth = 220;

const Sidebar = () => {
  const location = useLocation();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const mainMenuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon fontSize="small" />,
      path: '/'
    },
    {
      text: 'Vehicle Analytics',
      icon: <BatteryIcon fontSize="small" />,
      path: '/ev-analytics'
    },
    {
      text: 'Penetration',
      icon: <PenetrationIcon fontSize="small" />,
      path: '/penetration'
    },
    {
      text: 'Custom Dashboard',
      icon: <DashboardCustomizeIcon fontSize="small" />,
      path: '/custom-dashboard'
    },
    {
      text: 'Vahan Map',
      icon: <LocationOnIcon fontSize="small" />,
      path: '/vahan-map'
    }
  ];
  
  const supportMenuItems = [
    // { text: 'Settings', icon: <SettingsIcon fontSize="small" /> },
    // { text: 'Help', icon: <HelpIcon fontSize="small" /> }
  ];

  const handleMouseEnter = () => {
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    setIsExpanded(false);
  };

  const renderMenuItem = (item) => {
    const isActive = location.pathname === item.path;
    
    return (
      <Tooltip 
        title={isExpanded ? "" : item.text} 
        placement="right"
        arrow
      >
        <ListItem 
          button 
          key={item.text} 
          component={item.path ? Link : 'div'} 
          to={item.path}
          sx={{ 
            mb: 0.5,
            py: 0.75,
            px: isExpanded ? 1.5 : 0.5,
            borderRadius: '8px',
            justifyContent: isExpanded ? 'flex-start' : 'center',
            backgroundColor: isActive 
              ? alpha(theme.palette.primary.main, 0.08)
              : 'transparent',
            color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: isActive 
                ? alpha(theme.palette.primary.main, 0.12)
                : alpha(theme.palette.text.primary, 0.04),
            }
          }}
        >
          <ListItemIcon 
            sx={{ 
              minWidth: isExpanded ? 30 : 'auto', 
              color: isActive ? theme.palette.primary.main : theme.palette.text.secondary
            }}
          >
            {item.icon}
          </ListItemIcon>
          {isExpanded && (
            <ListItemText 
              primary={
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem'
                  }}
                >
                  {item.text}
                </Typography>
              } 
            />
          )}
        </ListItem>
      </Tooltip>
    );
  };

  return (
    <Drawer
      variant="permanent"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: isExpanded ? expandedDrawerWidth : collapsedDrawerWidth,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        [`& .MuiDrawer-paper`]: { 
          width: isExpanded ? expandedDrawerWidth : collapsedDrawerWidth, 
          overflowX: 'hidden',
          boxSizing: 'border-box',
          mt: 8,
          backgroundColor: 'background.default',
          borderRight: '1px solid rgba(229, 231, 235, 0.8)',
          boxShadow: 'none',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
        '& + .MuiBox-root': {
          marginLeft: '0 !important',
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ overflow: 'auto', mt: 2, px: isExpanded ? 2 : 1, flex: 1 }}>
          {isExpanded && (
            <Box sx={{ mb: 1.5, px: 1 }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Analytics
              </Typography>
            </Box>
          )}
          <List sx={{ p: 0 }}>
            {mainMenuItems.map(renderMenuItem)}
          </List>
          
          
          <List sx={{ p: 0, mt: isExpanded ? 0 : 4 }}>
            {supportMenuItems.map(renderMenuItem)}
          </List>
        </Box>
        
        {/* {isExpanded && (
          <Box sx={{ p: 2, borderTop: '1px solid rgba(229, 231, 235, 0.8)' }}>
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <Typography variant="body2" fontWeight={500} color="text.secondary" gutterBottom>
                Need more insights?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Export your analytics data with our premium features.
              </Typography>
            </Box>
          </Box>
        )} */}
      </Box>
    </Drawer>
  );
};

export default Sidebar; 